import {
  FIELD_MAPPING_CORNER_WAIT_MS,
  FIELD_MAPPING_MAX_GPS_ACCURACY_M,
  isGpsAccuracyAcceptableForFieldMapping,
} from './fieldMappingGps';
import { readGeolocationAccuracyM } from './geolocation';

/** Resilient browser geolocation — retries with relaxed options (helps macOS / Wi‑Fi fixes). */

const ATTEMPT_OPTIONS: PositionOptions[] = [
  { enableHighAccuracy: true, timeout: 20_000, maximumAge: 0 },
  { enableHighAccuracy: true, timeout: 25_000, maximumAge: 5_000 },
  { enableHighAccuracy: false, timeout: 30_000, maximumAge: 30_000 },
];

export function requestCurrentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(Object.assign(new Error('Geolocation not supported'), { code: 2 }));
      return;
    }

    let attempt = 0;

    const tryAttempt = (lastError?: GeolocationPositionError) => {
      if (attempt >= ATTEMPT_OPTIONS.length) {
        reject(lastError ?? Object.assign(new Error('Geolocation failed'), { code: 2 }));
        return;
      }
      const options = ATTEMPT_OPTIONS[attempt++];
      navigator.geolocation.getCurrentPosition(resolve, tryAttempt, options);
    };

    tryAttempt();
  });
}

const FIELD_MAPPING_WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 1000,
  timeout: 10_000,
};

export type WaitForFieldMappingPositionOptions = {
  timeoutMs?: number;
};

/**
 * Wait for a GNSS fix suitable for field mapping (same rule as live trace vertices).
 * Uses `watchPosition` with high accuracy — unlike {@link requestCurrentPosition}, does not
 * fall back to low-accuracy / stale cached fixes that often exceed the 15 m threshold.
 */
export function waitForFieldMappingPosition(
  options: WaitForFieldMappingPositionOptions = {},
): Promise<GeolocationPosition> {
  const timeoutMs = options.timeoutMs ?? FIELD_MAPPING_CORNER_WAIT_MS;

  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(Object.assign(new Error('Geolocation not supported'), { code: 2 }));
      return;
    }

    let watchId: number | null = null;
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let settled = false;
    let best: GeolocationPosition | null = null;
    let bestAccuracyM = Infinity;

    const cleanup = () => {
      if (watchId != null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
    };

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    const onPosition = (position: GeolocationPosition) => {
      const accuracyM = readGeolocationAccuracyM(position);
      if (accuracyM != null && accuracyM < bestAccuracyM) {
        best = position;
        bestAccuracyM = accuracyM;
      }
      if (isGpsAccuracyAcceptableForFieldMapping(accuracyM)) {
        finish(() => resolve(position));
      }
    };

    const onError = (error: GeolocationPositionError) => {
      finish(() => reject(error));
    };

    watchId = navigator.geolocation.watchPosition(
      onPosition,
      onError,
      FIELD_MAPPING_WATCH_OPTIONS,
    );

    timerId = setTimeout(() => {
      if (best && isGpsAccuracyAcceptableForFieldMapping(readGeolocationAccuracyM(best))) {
        finish(() => resolve(best!));
        return;
      }
      const reportedM = best ? readGeolocationAccuracyM(best) : null;
      const detail =
        reportedM != null
          ? `${Math.round(reportedM)} m (need ≤${FIELD_MAPPING_MAX_GPS_ACCURACY_M} m)`
          : 'no accurate fix yet';
      finish(() =>
        reject(
          new Error(
            `GPS accuracy too low (${detail}). Live trace keeps listening until GPS improves — wait for the map pill to show ±${FIELD_MAPPING_MAX_GPS_ACCURACY_M} m or less, stand still in the open, then tap Add corner again.`,
          ),
        ),
      );
    }, timeoutMs);
  });
}

export function watchPermissionState(
  onChange: (state: PermissionState) => void,
): (() => void) | undefined {
  if (!navigator.permissions?.query) return undefined;

  navigator.permissions
    .query({ name: 'geolocation' })
    .then((result) => {
      onChange(result.state);
      result.addEventListener('change', () => onChange(result.state));
    })
    .catch(() => onChange('prompt'));

  return undefined;
}
